import React from 'react';
import { Helmet } from 'react-helmet-async';

interface CanonicalTagProps {
  url: string;
  alternateUrls?: Array<{
    href: string;
    hreflang: string;
  }>;
}

export const CanonicalTag: React.FC<CanonicalTagProps> = ({ url, alternateUrls }) => {
  return (
    <Helmet>
      <link rel="canonical" href={url} />
      {alternateUrls?.map((alt, index) => (
        <link
          key={index}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}
    </Helmet>
  );
};

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string[];
  canonicalUrl: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
  noFollow?: boolean;
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
}

export const EnhancedMetaTags: React.FC<MetaTagsProps> = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage = 'https://www.carolinatrophyroofs.com/og-image.jpg',
  ogType = 'website',
  noIndex = false,
  noFollow = false,
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  geoRegion,
  geoPlacename,
  geoPosition
}) => {
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow'
  ].join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Carolina Trophy Roofs" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific */}
      {articleAuthor && <meta property="article:author" content={articleAuthor} />}
      {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
      {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@CarolinaTrophyRoofs" />
      
      {/* Geographic Tags */}
      {geoRegion && <meta name="geo.region" content={geoRegion} />}
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}
      {geoPosition && (
        <>
          <meta name="geo.position" content={geoPosition} />
          <meta name="ICBM" content={geoPosition.replace(';', ', ')} />
        </>
      )}
      
      {/* Business Info */}
      <meta name="business:contact_data:phone_number" content="(252) 302-2003" />
      <meta name="business:contact_data:email" content="info@carolinatrophyroofs.com" />
      <meta name="business:contact_data:website" content="https://www.carolinatrophyroofs.com" />
      
      {/* Additional SEO Tags */}
      <meta name="author" content="Carolina Trophy Roofs" />
      <meta name="publisher" content="Carolina Trophy Roofs" />
      <meta name="copyright" content="© 2025 Carolina Trophy Roofs" />
      <meta name="language" content="en" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS Prefetch for external resources */}
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
    </Helmet>
  );
};

interface PageMetaProps {
  pageType: 'homepage' | 'location' | 'blog' | 'service' | 'about' | 'contact';
  title: string;
  description: string;
  path: string;
  keywords?: string[];
  locationData?: {
    cityName: string;
    stateName: string;
    latitude: number;
    longitude: number;
  };
  articleData?: {
    author: string;
    publishedTime: string;
    modifiedTime: string;
  };
}

export const PageMeta: React.FC<PageMetaProps> = ({
  pageType,
  title,
  description,
  path,
  keywords = [],
  locationData,
  articleData
}) => {
  const baseUrl = 'https://www.carolinatrophyroofs.com';
  const canonicalUrl = `${baseUrl}${path}`;
  
  // Generate location-specific geo tags
  const geoData = locationData ? {
    geoRegion: locationData.stateName,
    geoPlacename: locationData.cityName,
    geoPosition: `${locationData.latitude};${locationData.longitude}`
  } : undefined;

  // Page-specific keywords
  const baseKeywords = ['roofing contractor', 'roof repair', 'roof replacement', 'North Carolina', 'Carolina Trophy Roofs'];
  const allKeywords = [...baseKeywords, ...keywords];

  return (
    <EnhancedMetaTags
      title={title}
      description={description}
      keywords={allKeywords}
      canonicalUrl={canonicalUrl}
      ogType={pageType === 'blog' ? 'article' : 'website'}
      articleAuthor={articleData?.author}
      articlePublishedTime={articleData?.publishedTime}
      articleModifiedTime={articleData?.modifiedTime}
      {...geoData}
    />
  );
};